import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Monitor, Smartphone, Tablet, X } from 'lucide-react';

interface SessionInfo {
  sessionId: string;
  deviceInfo: string;
  ipAddress: string;
  loginTime: string;
  lastActivityTime: string;
  isCurrent: boolean;
}

interface DuplicateLoginModalProps {
  existingSessions: SessionInfo[];
  onConfirm: () => void;
  onCancel: () => void;
}

export const DuplicateLoginModal: React.FC<DuplicateLoginModalProps> = ({
  existingSessions,
  onConfirm,
  onCancel,
}) => {
  const getDeviceIcon = (deviceInfo: string) => {
    const info = deviceInfo.toLowerCase();
    if (info.includes('mobile') || info.includes('android') || info.includes('iphone')) {
      return <Smartphone className="w-5 h-5 text-blue-600" />;
    }
    if (info.includes('tablet') || info.includes('ipad')) {
      return <Tablet className="w-5 h-5 text-blue-600" />;
    }
    return <Monitor className="w-5 h-5 text-blue-600" />;
  };

  const formatDateTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="relative">
          <button
            onClick={onCancel}
            className="absolute top-4 right-4 p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
          <CardTitle className="flex items-center gap-2 text-xl">
            <AlertCircle className="w-6 h-6 text-amber-600" />
            중복 로그인 감지
          </CardTitle>
          <CardDescription>
            다른 기기에서 로그인되어 있습니다. 계속 진행하시면 기존 세션이 모두 종료됩니다.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert variant="destructive" className="bg-amber-50 border-amber-200">
            <AlertCircle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800">
              강제 로그인 시 아래의 모든 세션이 강제 종료되며, 해당 기기에서는 즉시 로그아웃됩니다.
            </AlertDescription>
          </Alert>

          <div className="space-y-3">
            <h3 className="font-semibold text-sm text-gray-700">현재 로그인된 세션</h3>
            {existingSessions.map((session) => (
              <div
                key={session.sessionId}
                className="bg-gray-50 rounded-lg p-4 border border-gray-200"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-1">
                    {getDeviceIcon(session.deviceInfo)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium text-gray-900">{session.deviceInfo}</p>
                      {session.isCurrent && (
                        <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
                          현재 세션
                        </span>
                      )}
                    </div>
                    <div className="space-y-1 text-sm text-gray-600">
                      <p className="flex items-center gap-2">
                        <span className="font-medium">IP 주소:</span>
                        <span className="font-mono">{session.ipAddress}</span>
                      </p>
                      <p className="flex items-center gap-2">
                        <span className="font-medium">로그인 시간:</span>
                        <span>{formatDateTime(session.loginTime)}</span>
                      </p>
                      <p className="flex items-center gap-2">
                        <span className="font-medium">마지막 활동:</span>
                        <span>{formatDateTime(session.lastActivityTime)}</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              variant="outline"
              onClick={onCancel}
              className="flex-1"
            >
              취소
            </Button>
            <Button
              variant="destructive"
              onClick={onConfirm}
              className="flex-1"
            >
              강제 로그인
            </Button>
          </div>

          <p className="text-xs text-gray-500 text-center">
            본인의 로그인이 아닌 경우 비밀번호를 즉시 변경하시기 바랍니다.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
